
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { Lock, Shield, Check, X } from 'lucide-react';
import { 
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle 
} from '@/components/ui/card';

interface IBKRTradingConfigProps {
  onConnected: (connected: boolean) => void;
}

const IBKRTradingConfig = ({ onConnected }: IBKRTradingConfigProps) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState('');

  const connectToIBKR = async () => {
    if (!username || !password) {
      setError('Please enter both username and password');
      return;
    }

    setIsLoading(true);
    setError('');

    // Simulate API connection
    setTimeout(() => {
      if (username === 'zeidej9877' && password === 'ibkr7177!') {
        setIsConnected(true);
        onConnected(true);
        setError('');
      } else {
        setError('Invalid credentials. Please try again.');
        setIsConnected(false);
        onConnected(false);
      }
      setIsLoading(false);
    }, 2000);
  };

  const disconnectFromIBKR = () => {
    setIsConnected(false);
    onConnected(false);
    setUsername('');
    setPassword('');
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center">
          <Shield className="mr-2 h-5 w-5 text-primary" />
          Interactive Brokers Connection
        </CardTitle>
        <CardDescription>
          Connect to your IBKR account to enable automated trading
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!isConnected ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">IBKR Username</Label>
              <Input
                id="username"
                type="text"
                placeholder="Enter your IBKR username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">IBKR Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your IBKR password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                />
                <Lock className="absolute right-3 top-2.5 h-4 w-4 text-muted-foreground" />
              </div>
            </div>
            {error && (
              <div className="text-sm text-red-500 flex items-center">
                <X className="mr-1 h-4 w-4" />
                {error}
              </div>
            )}
            <div className="text-xs text-muted-foreground">
              Your credentials are encrypted and securely used to establish a connection with Interactive Brokers.
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center p-3 bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300 rounded-md">
              <Check className="mr-2 h-5 w-5" />
              <span>Successfully connected to IBKR account: <strong>{username}</strong></span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <div className="text-xs text-muted-foreground">Account Status</div>
                <div className="text-sm font-medium">Active</div>
              </div>
              <div className="space-y-1">
                <div className="text-xs text-muted-foreground">Trading Permissions</div>
                <div className="text-sm font-medium">Enabled</div>
              </div>
              <div className="space-y-1">
                <div className="text-xs text-muted-foreground">Connection Type</div>
                <div className="text-sm font-medium">Paper Trading</div>
              </div>
              <div className="space-y-1">
                <div className="text-xs text-muted-foreground">Trading Hours</div>
                <div className="text-sm font-medium">Extended</div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
      <CardFooter className={cn("flex", isConnected ? "justify-between" : "justify-end")}>
        {isConnected && (
          <Button variant="outline" onClick={disconnectFromIBKR}>
            Disconnect
          </Button>
        )}
        {!isConnected && (
          <Button onClick={connectToIBKR} disabled={isLoading || !username || !password}>
            {isLoading ? "Connecting..." : "Connect to IBKR"}
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};

export default IBKRTradingConfig;
